import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import managerService, { type ClientSummary } from '../../services/manager.service';
import portfolioService, { type ProductSummary } from '../../services/portfolio.service';
import proposalsService from '../../services/proposals.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { colors } from '../../styles/theme';
import { PROPOSAL_SKIP_LABEL, type Campaign, type CampaignResult } from '../../types/proposals';
import { readerLocale } from '../../i18n/language';

/**
 * Proposal campaigns (0070): one product offered to many EXISTING clients.
 * Prospects are never targeted — a product offer to someone without an account
 * risks general solicitation under Reg D 506(b). Every client is checked on
 * their own (active, no compliance hold, product suitable, no open proposal),
 * and the result lists who was skipped and why.
 */

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 8,
  border: `1px solid ${colors.border}`,
  background: colors.surface,
  color: colors.ink,
  fontSize: 14,
};

const cardStyle: React.CSSProperties = {
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: 16,
  padding: 20,
};

function fullName(c: ClientSummary): string {
  return `${c.first_name} ${c.last_name}`.trim() || c.email;
}

function ClientPicker({
  clients,
  selected,
  onToggle,
  onSelectVisible,
}: Readonly<{
  clients: ClientSummary[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  onSelectVisible: (ids: string[]) => void;
}>): JSX.Element {
  const [query, setQuery] = useState('');
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q
      ? clients.filter((c) => `${fullName(c)} ${c.email}`.toLowerCase().includes(q))
      : clients;
  }, [clients, query]);

  return (
    <div>
      <div className="flex gap-2 mb-2">
        <input
          placeholder="Search clients"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={inputStyle}
        />
        <button
          type="button"
          onClick={() => onSelectVisible(visible.map((c) => c.id))}
          className="px-3 rounded-lg text-sm whitespace-nowrap"
          style={{ border: `1px solid ${colors.border}`, color: colors.inkSoft }}
        >
          Select shown
        </button>
      </div>
      <div style={{ maxHeight: 260, overflowY: 'auto', border: `1px solid ${colors.borderSoft}`, borderRadius: 8 }}>
        {visible.map((c) => (
          <label key={c.id} className="flex items-center gap-2 px-3 py-2 text-sm" style={{ color: colors.ink }}>
            <input type="checkbox" checked={selected.has(c.id)} onChange={() => onToggle(c.id)} />
            <span>{fullName(c)}</span>
            <span className="text-xs" style={{ color: colors.muted }}>{c.email}</span>
          </label>
        ))}
        {visible.length === 0 && (
          <p className="px-3 py-2 text-sm" style={{ color: colors.muted }}>No clients match.</p>
        )}
      </div>
      <p className="text-xs mt-1" style={{ color: colors.muted }}>{selected.size} selected</p>
    </div>
  );
}

function CampaignResultPanel({
  result,
  clients,
}: Readonly<{ result: CampaignResult; clients: ClientSummary[] }>): JSX.Element {
  const byId = new Map(clients.map((c) => [c.id, c]));
  return (
    <section style={{ ...cardStyle, marginTop: 16 }} aria-live="polite">
      <p className="text-sm font-semibold" style={{ color: colors.ink }}>
        Sent {result.created_count} of {result.target_count} proposals.
      </p>
      {result.skipped.length > 0 && (
        <ul className="mt-2 text-sm" style={{ color: colors.inkSoft }}>
          {result.skipped.map((s) => {
            const client = byId.get(s.client_id);
            return (
              <li key={s.client_id}>
                {client ? fullName(client) : s.client_id}: {PROPOSAL_SKIP_LABEL[s.reason] ?? s.reason}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function RecentCampaigns({ campaigns }: Readonly<{ campaigns: Campaign[] }>): JSX.Element {
  return (
    <section style={{ ...cardStyle, marginTop: 16 }}>
      <h2 className="text-sm font-bold uppercase tracking-widest mb-3" style={{ color: colors.inkSoft }}>
        Recent campaigns
      </h2>
      {campaigns.length === 0 ? (
        <p className="text-sm" style={{ color: colors.muted }}>No campaigns yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr style={{ color: colors.muted, textAlign: 'left' }}>
              <th className="py-1">Name</th>
              <th className="py-1">Series</th>
              <th className="py-1">Sent</th>
              <th className="py-1">Open until</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((c) => (
              <tr key={c.id} style={{ borderTop: `1px solid ${colors.borderSoft}`, color: colors.ink }}>
                <td className="py-2">{c.name}</td>
                <td className="py-2">{c.product_name}</td>
                <td className="py-2">{c.created_count} / {c.target_count}</td>
                <td className="py-2">{new Date(c.expires_at).toLocaleDateString(readerLocale())}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

export default function ProposalCampaigns(): JSX.Element {
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [clients, setClients] = useState<ClientSummary[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [productId, setProductId] = useState('');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [days, setDays] = useState('30');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CampaignResult | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [productRows, clientRows, campaignRows] = await Promise.all([
        portfolioService.listProducts(),
        managerService.listClients(),
        proposalsService.listCampaigns(),
      ]);
      // /portfolio/products already excludes the synthetic onboarding host
      // server-side (it has no funds vehicle), so every row here is offerable.
      setProducts(productRows);
      setProductId((current) => current || productRows[0]?.id || '');
      setClients(clientRows);
      setCampaigns(campaignRows);
    } catch (e: unknown) {
      setLoadError(getApiErrorMessage(e, 'Could not load the Series, clients or campaigns.'));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  function toggle(id: string): void {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const outcome = await proposalsService.createCampaign({
        product_id: productId,
        name: name.trim(),
        client_ids: [...selected],
        message: message.trim() || null,
        expires_in_days: Number.parseInt(days, 10) || 30,
      });
      setResult(outcome);
      setSelected(new Set());
      setCampaigns(await proposalsService.listCampaigns());
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Could not run the campaign.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ padding: 24 }}>
      <PageHeader
        title="Proposal campaigns"
        subtitle="Offer one Series to existing clients. Each client sees it on their dashboard and decides on their own."
      />
      {loadError && (
        <div className="rounded-lg p-3 mb-4 text-sm flex gap-3" role="alert" style={{ background: colors.dangerBg, color: colors.danger }}>
          <span>{loadError}</span>
          <button type="button" className="underline" onClick={() => { void load(); }}>Retry</button>
        </div>
      )}
      <form onSubmit={(e) => { void submit(e); }} style={cardStyle}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="grid gap-3">
            <label className="text-xs font-semibold" style={{ color: colors.muted }}>
              <span>Campaign name</span>
              <input value={name} minLength={3} maxLength={120} onChange={(e) => setName(e.target.value)} style={inputStyle} />
            </label>
            <label className="text-xs font-semibold" style={{ color: colors.muted }}>
              <span>Series</span>
              <select value={productId} onChange={(e) => setProductId(e.target.value)} style={inputStyle}>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </label>
            <label className="text-xs font-semibold" style={{ color: colors.muted }}>
              <span>Message to clients (optional)</span>
              <textarea value={message} maxLength={1000} rows={4} onChange={(e) => setMessage(e.target.value)} style={inputStyle} />
            </label>
            <label className="text-xs font-semibold" style={{ color: colors.muted }}>
              <span>Open for (days)</span>
              <input type="number" min={1} max={90} value={days} onChange={(e) => setDays(e.target.value)} style={inputStyle} />
            </label>
          </div>
          <ClientPicker
            clients={clients}
            selected={selected}
            onToggle={toggle}
            onSelectVisible={(ids) => setSelected((current) => new Set([...current, ...ids]))}
          />
        </div>
        {error && <p className="text-sm mt-3" role="alert" style={{ color: colors.danger }}>{error}</p>}
        <div className="flex justify-end mt-4">
          <button
            type="submit"
            disabled={submitting || selected.size === 0 || !productId || name.trim().length < 3}
            className="px-4 py-2 rounded-lg text-sm font-semibold"
            style={{ background: colors.ink, color: colors.surface }}
          >
            {submitting ? 'Sending…' : `Send to ${selected.size} client(s)`}
          </button>
        </div>
      </form>
      {result && <CampaignResultPanel result={result} clients={clients} />}
      <RecentCampaigns campaigns={campaigns} />
    </div>
  );
}
